import { useParams } from 'react-router';
import { useOutletContext } from 'react-router-dom';
import pageRules from '@/constants/defaultPageRules/sandbox/pageRules.json';
import type { OutletData } from '@/types/OutletData';
import { useAirKeyTracePageView } from '@/utils/airKeyTrace';
import { Spinner } from '@cof/omni-react';
import { IPhoneScreen } from '@components/3ds';
import { Facts, useRulesEngine } from '@hooks/useRulesEngine';
import { NFCNotEnabled } from '@pages/DeviceError/NFCNotEnabled';
import { SandboxMessageScreen } from '@/components/Sandbox/SandboxMessageScreen/SandboxMessageScreen';
import { TapScreen } from '@/components/Sandbox/TapScreen/TapScreen';
import { CopyUrlButton } from '@components/global/CopyUrlButton/CopyUrlButton';

export const Sandbox = () => {
  useAirKeyTracePageView('loading');

  const { processId } = useParams();
  const { bowser, loading } = useOutletContext<OutletData>();

  const rulesResult = useRulesEngine({
    facts: bowser as Facts,
    rules: pageRules as any,
  });

  if (loading) return <Spinner data-testid="Sandbox--Spinner" size="giant" />;

  if (rulesResult === 'GenericError' || !processId) {
    return <SandboxMessageScreen title="Error" body="An unknown error has occurred" />;
  }

  if (rulesResult === 'NotSupported') {
    return (
      <SandboxMessageScreen
        title="Incompatible Device"
        body="The device you are using does not support AirKey taps. You can return to the developer portal or contact the AirKey team if you believe this is in error."
      />
    );
  }

  if (
    rulesResult === 'InvalidBrowserIPhone' ||
    rulesResult === 'InvalidBrowserAndroid'
  ) {
    const suggestedBrowser =
      rulesResult === 'InvalidBrowserIPhone' ? 'Safari' : 'Chrome';
    return (
      <SandboxMessageScreen
        title="This browser is not supported"
        body={`Please copy this link into ${suggestedBrowser} to continue:`}
      >
        <div style={{ width: '100%' }}>
          <CopyUrlButton message="" />
        </div>
      </SandboxMessageScreen>
    );
  }

  if (rulesResult === 'PrivateModeIPhone' || rulesResult === 'PrivateModeAndroid') {
    return (
      <SandboxMessageScreen
        title="Turn off private browsing"
        body="To continue, you'll need to turn off private browsing mode on this device"
      >
        {rulesResult === 'PrivateModeIPhone' && (
          <p>
            <a href="https://support.apple.com/en-us/105030" style={{ color: 'white' }}>
              Here&apos;s how to do it.
            </a>
          </p>
        )}
      </SandboxMessageScreen>
    );
  }

  if (rulesResult === 'EnableNfc') {
    return <NFCNotEnabled />;
  }

  if (rulesResult === 'IsValidIPhone') {
    return <IPhoneScreen />;
  }

  return <TapScreen processId={processId} />;
};
